import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import moment, { Moment } from 'moment';
import { BehaviorSubject, catchError, EMPTY, Observable, tap, throwError } from 'rxjs';
import { User } from 'src/app/shared/models/user.model';
import * as uuid from 'uuid';

const USER_DATA_LOCAL_STORAGE_KEY = 'user';
const FIRST_TIME_USER_LOGIN_LOCAL_STORAGE_KEY = 'firstTimeUserLogin';

interface Token {
  sub: string;
  iat: number;
  nbf: number;
  jti: string;
  exp: number;
  type: string;
  user_id: string;
  device_id: string;
}

interface AccessToken extends Token {
  fresh: boolean;
}

export interface RefreshResponseData {
  accessToken: string;
}

interface LoginResponseData extends RefreshResponseData {
  refreshToken: string;
}

export interface UserData {
  id: string;
  email: string;
  _accessToken: string;
  _refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  user = new BehaviorSubject<User | null>(null);
  public user$ = this.user.asObservable();

  getApiPortalFirst(): Observable<any> {
    return this.http.get('/api/v1/customer-user/first');
  }

  generateLoginCode(email: string) {
    return this.http.get('/api/v1/customer-user/login', {
      params: { email: email },
      headers: { 'Device-Id': this.getDeviceId() },
    });
  }

  login(email: string, code: string): Observable<LoginResponseData> {
    return this.http
      .post<LoginResponseData>(
        '/api/v1/customer-user/login',
        {
          email: email,
          code: code,
        },
        {
          headers: { 'device-id': this.getDeviceId() },
        }
      )
      .pipe(
        tap((responseData: LoginResponseData) => {
          const decodedToken: AccessToken = this.decodeAccessToken(responseData.refreshToken);

          const user = new User(decodedToken.sub, email, responseData.accessToken, responseData.refreshToken);

          this.user.next(user);
          this.storeUserToLocalStorage(user);

          this.navigateToOrdersPage();
        }),
        catchError((errorResponse) => {
          return this.handleErrorResponse(errorResponse);
        })
      );
  }

  autoLogin(takeToOrdersPage: boolean = false): void {
    const user: UserData | null = this.getUserFromLocalStorage();

    if (!user) {
      return;
    }

    const loadedUser: User = new User(user.id, user.email, user._accessToken, user._refreshToken);

    if (!loadedUser.refreshToken) {
      return;
    }

    this.user.next(loadedUser);

    if (takeToOrdersPage) {
      this.navigateToOrdersPage();
    }
  }

  logout() {
    localStorage.removeItem('user');
    this.user.next(null);

    this.router.navigate(['/login']).catch(() => {});
  }

  handleErrorResponse(errorResponse: any): Observable<never> {
    console.error(errorResponse);
    let errorMessage: string = 'An unknown error occurred';

    if (errorResponse.status == 401 && errorResponse.error?.detail) {
      errorMessage = errorResponse.error.detail;
    }

    return throwError(() => new Error(errorMessage));
  }

  refreshToken(): Observable<RefreshResponseData> {
    const user: UserData | null = this.getUserFromLocalStorage();

    if (!user) {
      return EMPTY;
    }

    const refreshToken: string = user._refreshToken;

    return this.http
      .post<RefreshResponseData>('/api/v1/customer-user/refresh', null, {
        headers: {
          'device-id': this.getDeviceId(),
          Authorization: `Bearer ${refreshToken}`,
        },
      })
      .pipe(
        tap((responseData: RefreshResponseData) => {
          user._accessToken = responseData.accessToken;
          this.storeUserToLocalStorage(user);

          const loadedUser: User = new User(user.id, user.email, user._accessToken, user._refreshToken);

          this.user.next(loadedUser);
        }),
        catchError((errorResponse) => {
          return this.handleErrorResponse(errorResponse);
        })
      );
  }

  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  decodeAccessToken(accessToken: string): AccessToken {
    return this.decodeToken(accessToken);
  }

  isTokenExpiring(accessToken: string): boolean {
    const decodedToken: AccessToken = this.decodeAccessToken(accessToken);
    const tokenExpiry: Moment = moment.unix(decodedToken.exp);
    return tokenExpiry.diff(moment()) < 5;
  }

  getUserFromLocalStorage(): UserData | null {
    if (!localStorage.getItem(USER_DATA_LOCAL_STORAGE_KEY)) {
      return null;
    }

    return JSON.parse(String(localStorage.getItem(USER_DATA_LOCAL_STORAGE_KEY)));
  }

  storeUserToLocalStorage(user: User | UserData): void {
    localStorage.setItem(USER_DATA_LOCAL_STORAGE_KEY, JSON.stringify(user));
  }

  getDeviceId(): string {
    let deviceId: string | null = localStorage.getItem('deviceId');

    if (!deviceId) {
      deviceId = uuid.v4();
      localStorage.setItem('deviceId', deviceId);
    }

    return deviceId;
  }

  navigateToOrdersPage() {
    this.router.navigate(['/customer/customer-orders']).catch(() => {});
  }

  isFirstTimeUserLoginLocalStorageKey(): boolean | null {
    return !localStorage.getItem(FIRST_TIME_USER_LOGIN_LOCAL_STORAGE_KEY);
  }

  storeFirstTimeUserLoginLocalStorageKey(): void {
    localStorage.setItem(FIRST_TIME_USER_LOGIN_LOCAL_STORAGE_KEY, 'true');
  }
}
